import { call, put, takeEvery } from "redux-saga/effects";

import { ConditionParam, ConditionParams } from "@multiweb/clients/dataReader";
import { bridge } from "@multiweb/reactCore/bridge";
import { IRunMethodResult } from "@multiweb/types/contracts";
import { CancellationToken, IOWObject } from "@multiweb/types/multiweb";
import { SagaWithReturnType, SagaYieldReturn } from "@multiweb/types/react";
import { ConditionOperator, StringUtils } from "Global/Base/Utils/StringUtils";
import { createClearAction, createSetResultAction } from "./action";
import { INIT } from "./constants";
import { reportingScalarLogger } from "./logger";
import { IInitAction, IResultFromDataReaderParams } from "./types";

const { getDataReaderClient, getFormattedNumberString, runStaticMethodWithParams, getInstance } = bridge;

export const saga: SagaWithReturnType = function* reportingScalarSaga() {
    yield takeEvery([INIT], loadReportingScalarResultSaga);
};

/**
 * Obsluhuje načtení dat
 */
export const loadReportingScalarResultSaga: SagaWithReturnType<[IInitAction]> = function* loadReportingScalarResultSaga(action) {
    const { aggregationFunction, className, attrName, condition, conditionParams, displayFormat, formatProviderKey, methodName, instanceId } = action.payload;
    const { dataKey } = action.meta;

    try {
        let result: string = "";
        let actualObject: IOWObject | undefined;

        if (instanceId)
            actualObject = yield call(getInstance, instanceId);

        //komponenta na detailFramu, načítá hodnotu z actualObjectu
        if (actualObject && attrName && !aggregationFunction) {
            const getValue = actualObject.getValue.bind(actualObject);
            result = yield call(getValue, attrName);
        } else {
            if (attrName && aggregationFunction) {
                const condParamsAttr = yield call(createReaderConditionParams, actualObject, conditionParams);
                const finalCondition = StringUtils.combineConditions(ConditionOperator.and, "ID <> 0", condition);
                result = yield call(loadResultFromDataReaderSaga, { aggregationFunction, className, attrName, condition: finalCondition, conditionParams: condParamsAttr });
            } else if (methodName) {
                result = yield call(loadResultFromStaticMethod, className, methodName, conditionParams);
            }
        }

        //pokud je výsledkem dotazu null, nechci nechat prázdné pole
        if (!result)
            result = "0";

        const formattedResults = getFormattedNumberString(result, { displayFormat: displayFormat }, formatProviderKey);

        yield put(createSetResultAction(dataKey, formattedResults));
    } catch (error: any) {
        yield call(reportingScalarLogger.error, "Loading of aggregations failed: %s. Input parameters: %O", error.message, {
            instanceId,
            className,
            condition,
            conditionParams,
        });
        // při chybě vymažeme předchozí výsledek, protože již není aktuální
        yield put(createClearAction(dataKey));
    }
};

/**
 * Načítá data pomocí dataReaderClienta
 * @param params parametry pro načtení dat z dataReaderClienta
 * @returns agregovaná hodnota
 */
const loadResultFromDataReaderSaga: SagaWithReturnType<[IResultFromDataReaderParams], string> = function* loadReportingScalarResultSaga(params) {
    const { aggregationFunction, className, attrName, condition, conditionParams } = params;

    const dataReaderClient: ReturnType<typeof getDataReaderClient> = yield call(getDataReaderClient);
    return yield call(dataReaderClient.getAggregation.bind(dataReaderClient), aggregationFunction, className, attrName, condition, conditionParams);
};

/**
 * Načtení dat ze statické metody
 * @param className třída na které je statická metoda
 * @param methodName statická metoda vracející hodnotu
 * @param params parametry statické metody
 * @returns hodnota vrácená statickou metodou
 */
const loadResultFromStaticMethod: SagaWithReturnType<[string, string, ConditionParams], IRunMethodResult> = function* loadResultFromStaticMethod(className, methodName, params) {
    const result: SagaYieldReturn<typeof runStaticMethodWithParams> = yield call(runStaticMethodWithParams, { className, methodName, params: params?.map(x => x) }, new CancellationToken());

    return result.resultValue;
};

/** Vytvoří kolekci parametrů
 * @param actualObject objekt ze kterého se má načíst hodnota pro parametr
 * @param conditionParamsAttrs vstupní atributy
 * @returns seznam hodnot parametrů pro podmínky
 */
const createReaderConditionParams: SagaWithReturnType<[any, ConditionParams], ConditionParams> = function* createReaderConditionParams(actualObject, conditionParamsAttrs) {
    if (actualObject && conditionParamsAttrs) {
        const condParam: ConditionParam[] = [];
        for (const param of conditionParamsAttrs) {
            const getValue = actualObject.getValue.bind(actualObject);
            condParam.push(yield call(getValue, param));
        }

        return condParam;
    } else
        return conditionParamsAttrs;
};
